/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useMemo } from 'react';
import type { OptionTypeBase } from 'react-select';
import type { StaffGetFirstAdminEmail, Teacher } from '@whizz/api-model/dist/@types';
import type { AxiosError } from 'axios';
import type { FormikHelpers } from 'formik';
import type { CreateStaffProps, StaffPost, UpdateStaffProps } from '@whizz/api-model';
import type { ToggleModalProps } from '../../../../../components/modal/types';
import type { OptionType } from '../../../../../util/types';
import { useI18n } from '@whizz/react-i18n';
import { useIdentity } from '@whizz/react-identity';
import { Button, ChoiceGroup, FieldGroup, Input, Loader, Selector } from '../../../../../components';
import { CancelButtonModal, FormGenericError, PhoneCodeSearch, SaveButton } from '../../../../../global';
import { GLOBAL_ICONS, QUERIES_IDS } from '../../../../../util/vars';
import { API_ERRORS } from '../../../../../util/constants';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { repository } from '@whizz/api-client';
import { useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { JobTypeEnum, User } from '@whizz/api-model';
import { ModalFooter } from '../../../../../components/modal/Modal';

const MIN_LABEL_WIDTH = 150;

const AddEditInitialiseStaffModal = ({
  mode = 'add',
  toggle,
  toggleConfirm,
  data,
  onSuccess,
  onError,
}: Props): JSX.Element => {
  const { translate } = useI18n();
  const queryClient = useQueryClient();
  const history = useHistory();

  const jobTypes = Object.entries(JobTypeEnum).filter((item) => 'number' === typeof item[1])
    .map((jobType) => ({ 'label': translate(`pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.JOB_TITLES.${jobType[0]}`), 'value': jobType[1] }));

  const { me } = useIdentity();

  const teacherId = (me as Teacher).getId() as number;

  const initMode = mode === 'init';
  const editMode = mode === 'edit';
  const addMode = mode === 'add';

  const recoveryEmailAdminQuery = useQuery<StaffGetFirstAdminEmail>(
    [ QUERIES_IDS.ROSTER_MANAGEMENT.RECOVERY_EMAIL ],
    () => repository.StaffRepository.getFirstAdminEmail(teacherId),
    {
      enabled: addMode,
      placeholderData: '',
    },
  );

  const validationSchema = useMemo(() => {
    let VALIDATION_SCHEMA = Yup.object().shape({
      jobType: Yup.number().nullable().required(translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.VALIDATIONS.JOB_TYPE.REQUIRED')),
      jobTitle: Yup.string(),
      firstName: Yup.string().required(translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.VALIDATIONS.FIRST_NAME.REQUIRED')),
      lastName: Yup.string().required(translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.VALIDATIONS.LAST_NAME.REQUIRED')),
      hasEmail: Yup.boolean(),
      email: Yup.string()
        .email(translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.VALIDATIONS.EMAIL.REQUIRED'))
        .when(
          'hasEmail',
          {
            is: true,
            then: (schema) => schema.required(translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.VALIDATIONS.EMAIL.REQUIRED')),
          },
        )
      ,
      recoveryEmail: Yup.string()
        .email(translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.VALIDATIONS.EMAIL.REQUIRED'))
        .when(
          'hasEmail',
          {
            is: false,
            then: (schema) => schema.required(translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.VALIDATIONS.EMAIL.REQUIRED')),
          },
        )
      ,
      phoneNumber: Yup.string(),
      phoneNationalCode: Yup.string(),
    });

    if (initMode) {
      VALIDATION_SCHEMA = VALIDATION_SCHEMA
        .shape({
          password: Yup.string().test(
            'password validation',
            translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.VALIDATIONS.PASSWORD.TEST'),
            (a: string | undefined) => User.validatePassword(a ?? ''),
          ).required(translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.VALIDATIONS.PASSWORD.REQUIRED')),
          confirmPassword: Yup.string()
            .required(translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.VALIDATIONS.CONFIRM_PASSWORD.REQUIRED'))
            .oneOf([ Yup.ref('password'), null ], translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.VALIDATIONS.CONFIRM_PASSWORD.NOT_MATCHING')),
        });
    }

    return VALIDATION_SCHEMA;
  }, []);

  const mutations = {
    create: useMutation(
      ({ data }: { data: CreateStaffProps, formikHelpers: FormikHelpers<FormData> }) => repository.StaffRepository.addStaff(data),
      {
        onSuccess: (data) => {
          queryClient.invalidateQueries([ QUERIES_IDS.STAFF ]);

          if (initMode) {
            history.push('/');
          } else {
            toggle(false);
          }

          onSuccess?.(data);
        },
        onError: (error: AxiosError<{ context: { hint: string } }>, { formikHelpers }) => {
          if (null == error.response) {
            return;
          }
          if (API_ERRORS.EMAIL_ALREADY_EXISTS === error.response.data.context.hint) {
            formikHelpers.setFieldError('email', translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.VALIDATIONS.EMAIL.ALREADY_EXISTS'));
          } else {
            onError?.();
          }
        },
      },
    ),
    edit: useMutation(
      ({ teacherId, data }: { teacherId: number, data: UpdateStaffProps, formikHelpers: FormikHelpers<FormData> }) => repository.StaffRepository.updateStaff(teacherId, data),
      {
        onSuccess: () => {
          queryClient.invalidateQueries([ QUERIES_IDS.STAFF ]);

          if (initMode) {
            history.push('/');
          } else {
            toggle(false);
          }

          onSuccess?.();
        },
        onError: (error: AxiosError<{ context: { hint: string } }>, { formikHelpers }) => {
          if (null == error.response) {
            return;
          }
          if (API_ERRORS.EMAIL_ALREADY_EXISTS === error.response.data.context.hint) {
            formikHelpers.setFieldError('email', translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.VALIDATIONS.EMAIL.ALREADY_EXISTS'));
          } else {
            onError?.();
          }
        },
      },
    ),
  };

  const handleDelete = () => {
    toggle(false);
    toggleConfirm(true);
  };

  const handleSubmit = (formData: FormData, formikHelpers: FormikHelpers<FormData>) => {
    const teacherData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      jobType: formData.jobType!,
      jobTitle: formData.jobTitle,
      email: formData.hasEmail ? formData.email : null,
      phoneNationalCode: formData.phoneNationalCode!,
      phoneNumber: formData.phoneNumber,
      recoveryEmail: !formData.hasEmail ? formData.recoveryEmail : null,
      password: formData.password,
    };

    if (editMode || initMode) {
      mutations.edit.mutate({ teacherId: data!.id, data: teacherData, formikHelpers });
    } else {
      mutations.create.mutate({ data: teacherData, formikHelpers });
    }
  };

  const isLoading = addMode && (!recoveryEmailAdminQuery.isFetched || recoveryEmailAdminQuery.isFetching);
  const allowDelete = editMode && teacherId !== data?.id;

  if (isLoading) {
    return (<Loader />);
  }

  return (
    <Formik<FormData>
      initialValues={{
        jobType: null,
        jobTitle: '',
        firstName: '',
        lastName: '',
        username: '',
        email: '',
        hasEmail: true,
        recoveryEmail: recoveryEmailAdminQuery.data,
        password: '',
        confirmPassword: '',
        showPassword: false,
        phoneNationalCode: '',
        phoneNumber: '',
        ...(
          editMode || initMode
            ? {
              jobType: data!.jobType,
              jobTitle: data!.jobTitle ?? '',
              firstName: data!.firstName,
              lastName: data!.lastName ?? '',
              username: data!.username,
              email: data!.email ?? '',
              hasEmail: data!.email === data!.username,
              recoveryEmail: data!.email === data!.username ? '' : data!.email!,
              password: data!.password,
              confirmPassword: data!.confirmPassword,
              phoneNationalCode: data!.phoneNationalCode,
              phoneNumber: data!.phoneNumber ?? '',
            }
            : null
        ),
      }}
      validationSchema={validationSchema}
      onSubmit={(formData, formikHelpers) => handleSubmit(formData, formikHelpers)}
    >
      {({ values, errors, handleSubmit, setFieldValue, resetForm, isValid, touched, getFieldProps }) => (
        <form onSubmit={handleSubmit}>
          <div className={'container space-y-8'}>
            <Selector
              id={'jobType'}
              label={translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.JOB_TYPE')}
              labelStyle={'bold'}
              hint={translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.HINTS.JOB_TYPE')}
              options={jobTypes}
              value={null != values.jobType ? jobTypes.find((option) => option.value === values.jobType) : null}
              error={touched.jobType ? errors.jobType : null}
              onChange={(selectedOption: OptionType) => setFieldValue('jobType', selectedOption?.value)}
              required
            />
            <Input
              {...getFieldProps('jobTitle')}
              id={'jobTitle'}
              label={translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.JOB_TITLE')}
              labelStyle={'bold'}
              hint={translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.HINTS.JOB_TITLE')}
              minWidthForLabel={MIN_LABEL_WIDTH}
              error={touched.jobType ? errors.jobTitle : null}
            />
            <Input
              {...getFieldProps('firstName')}
              id={'firstName'}
              label={translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.FIRST_NAME')}
              labelStyle={'bold'}
              minWidthForLabel={MIN_LABEL_WIDTH}
              error={touched.firstName ? errors.firstName : null}
              required
            />
            <Input
              {...getFieldProps('lastName')}
              id={'lastName'}
              label={translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.LAST_NAME')}
              labelStyle={'bold'}
              minWidthForLabel={MIN_LABEL_WIDTH}
              error={touched.lastName ? errors.lastName : null}
              required
            />
            <div className={'space-y-4'}>
              {((initMode || editMode) && !values.hasEmail)
                ? (
                  <Input
                    {...getFieldProps('username')}
                    id={'username'}
                    label={translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.USERNAME')}
                    labelStyle={'bold'}
                    type={'username'}
                    minWidthForLabel={MIN_LABEL_WIDTH}
                    readOnly
                  />
                ) : values.hasEmail ? (
                  <Input
                    {...getFieldProps('email')}
                    id={'email'}
                    label={translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.EMAIL')}
                    labelStyle={'bold'}
                    hint={translate(`pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.HINTS.${initMode ? 'EMAIL' : 'EMAIL_STAFF'}`)}
                    type={'email'}
                    minWidthForLabel={MIN_LABEL_WIDTH}
                    error={touched.email ? errors.email : null}
                    disabled={!values.hasEmail}
                    required={values.hasEmail}
                  />
                ) : null }
              {!editMode && (
                <ChoiceGroup
                  id={'no_email'}
                  label={translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.NO_EMAIL')}
                  checked={!values.hasEmail}
                  onChange={(event) => {
                    setFieldValue('hasEmail', !event.target.checked);

                    if (addMode && '' === values.recoveryEmail && values.recoveryEmail !== recoveryEmailAdminQuery.data) {
                      setFieldValue('recoveryEmail', recoveryEmailAdminQuery.data);
                    }
                  }}
                />
              )}
              {!values.hasEmail && (
                <Input
                  {...getFieldProps('recoveryEmail')}
                  id={'recoveryEmail'}
                  label={translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.NO_EMAIL_HINT.EMAIL')}
                  labelStyle={'bold'}
                  type={'email'}
                  error={touched.recoveryEmail ? errors.recoveryEmail : null}
                  required
                />
              )}
            </div>
            {initMode && (
              <>
                <Input
                  {...getFieldProps('password')}
                  id={'password'}
                  label={translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.PASSWORD')}
                  labelStyle={'bold'}
                  type={values.showPassword ? 'text' : 'password'}
                  minWidthForLabel={MIN_LABEL_WIDTH}
                  error={touched.password ? errors.password : null}
                  icon={GLOBAL_ICONS[values.showPassword ? 'see_off' : 'see_on']}
                  onIconClick={() => setFieldValue('showPassword', !values.showPassword)}
                  required
                />
                <Input
                  {...getFieldProps('confirmPassword')}
                  id={'confirmPassword'}
                  label={translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.CONFIRM_PASSWORD')}
                  labelStyle={'bold'}
                  type={values.showPassword ? 'text' : 'password'}
                  minWidthForLabel={MIN_LABEL_WIDTH}
                  error={touched.confirmPassword ? errors.confirmPassword : null}
                  icon={GLOBAL_ICONS[values.showPassword ? 'see_off' : 'see_on']}
                  onIconClick={() => setFieldValue('showPassword', !values.showPassword)}
                  required
                />
              </>
            )}
            <FieldGroup
              id={'phone'}
              label={translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.PHONE_NUMBER')}
              labelStyle={'bold'}
              hint={translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.HINTS.PHONE_NUMBER')}
              error={touched.phoneNumber ? errors.phoneNumber : null}
            >
              <div className={'flex items-center space-x-2 w-full'}>
                <PhoneCodeSearch
                  selectedValue={values.phoneNationalCode}
                  defaultCountry={(me as Teacher).country ?? 'gb'}
                  handleChange={(option: OptionTypeBase) => setFieldValue('phoneNationalCode', option?.value)}
                />
                <div className={'w-full'}>
                  <Input
                    {...getFieldProps('phoneNumber')}
                    id={'phoneNumber'}
                    hideLabel
                    isInline
                    label={''}
                    type={'tel'}
                  />
                </div>
              </div>
            </FieldGroup>

            {(!isValid && Object.keys(errors).some((key) => touched[key as keyof FormData]))
            && (<FormGenericError />)}
          </div>
          <ModalFooter justify={editMode ? 'between' : 'end'}>
            {editMode && (
              <CancelButtonModal
                align={'start'}
                onClick={() => resetForm()}
              >
                {translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.BUTTONS.DISCARD')}
              </CancelButtonModal>
            )}
            <SaveButton
              type={'submit'}
              showConfirmLabel={initMode}
              isLoading={mutations.create.isLoading || mutations.edit.isLoading}
            />
          </ModalFooter>
          {allowDelete
            ? (
              <div className={'container space-y-6'}>
                <h5 className={'font-bold'}>{translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.DELETE_ACCOUNT_TITLE')}</h5>
                <Button
                  color={'negative'}
                  icon={GLOBAL_ICONS.delete}
                  isOutline
                  onClick={handleDelete}
                >
                  {translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.BUTTONS.DELETE_ACCOUNT')}
                </Button>
              </div>
            )
            : null}
        </form>
      )}
    </Formik>
  );
};

export default AddEditInitialiseStaffModal;


export interface Props extends Pick<ToggleModalProps, 'toggle'> {
  mode?: 'init' | 'add' | 'edit';
  toggleConfirm(isOpen: boolean): void;

  data?: DataToEdit | null;
  onSuccess?: (data?: StaffPost) => void;
  onError?: () => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  jobType: number | null;
  jobTitle: string;
  email: string;
  phoneNumber: string;
  phoneNationalCode: string | null;
  recoveryEmail?: string;
  username?: string;
  password?: string;
  confirmPassword?: string;
  // Extra
  hasEmail: boolean;
  showPassword: boolean;
}

export interface DataToEdit {
  id: number;
  firstName: string;
  lastName?: string | null;
  jobType: number;
  jobTitle?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  phoneNationalCode?: string | null;
  recoveryEmail?: string;
  username?: string;
  password?: string;
  confirmPassword?: string;
}
